import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";

import { MDSCard, MDSCardVariant, MDSSkeleton, MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import { AgentPageHeader } from "@/pages/agent/components/agent-page-header";
import { AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { AgentGoalCard } from "@/pages/agent/goals/agent-goal-card";
import type { AgentGoal } from "@/pages/agent/intent-graph/agent-intent-graph.types";
import { useAgentIntentGraph } from "@/pages/agent/intent-graph/use-agent-intent-graph";
import { useAppStore } from "@/store";

type GoalTileViewModel = {
  goal: AgentGoal;
  descendants: Array<{ goal: AgentGoal; depth: number }>;
};

const buildGoalTiles = (goals: AgentGoal[]): GoalTileViewModel[] => {
  const goalsById = new Map(goals.map(goal => [goal.id, goal]));
  const childrenByParentId = new Map<string, AgentGoal[]>();

  for (const goal of goals) {
    if (!goal.parent_goal_id || !goalsById.has(goal.parent_goal_id)) {
      continue;
    }

    const children = childrenByParentId.get(goal.parent_goal_id) ?? [];
    children.push(goal);
    childrenByParentId.set(goal.parent_goal_id, children);
  }

  const visited = new Set<string>();
  const buildTile = (goal: AgentGoal): GoalTileViewModel => {
    const descendants: GoalTileViewModel["descendants"] = [];
    visited.add(goal.id);

    const visitChildren = (parentGoal: AgentGoal, depth: number) => {
      for (const childGoal of childrenByParentId.get(parentGoal.id) ?? []) {
        if (visited.has(childGoal.id)) {
          continue;
        }

        visited.add(childGoal.id);
        descendants.push({ goal: childGoal, depth });
        visitChildren(childGoal, depth + 1);
      }
    };

    visitChildren(goal, 1);

    return { goal, descendants };
  };

  const goalTiles = goals
    .filter(goal => !goal.parent_goal_id || !goalsById.has(goal.parent_goal_id))
    .map(buildTile);

  for (const goal of goals) {
    if (!visited.has(goal.id)) {
      goalTiles.push(buildTile(goal));
    }
  }

  return goalTiles;
};

export const AgentGoalsPage = observer(() => {
  const { store } = useAppStore();
  const graph = useAgentIntentGraph();
  const projectsById = new Map(
    store.assistantProjects.currentAssistantModels.map(project => [project.id, project])
  );

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Goals}>
      <AgentPageHeader
        description="The outcomes that organize your active projects and tasks."
        title="Goals"
      />
      {graph.isLoading ? (
        <MDSCard aria-label="Loading goals" variant={MDSCardVariant.Chrome}>
          <MDSSkeleton height={24} width="45%" />
        </MDSCard>
      ) : graph.isError ? (
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          Goals could not be loaded.
        </MDSText>
      ) : (graph.data?.goals ?? []).length === 0 ? (
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          No goals yet.
        </MDSText>
      ) : (
        <StyledGrid>
          {buildGoalTiles(graph.data?.goals ?? []).map(({ goal, descendants }) => {
            const connectedGoalIds = [goal, ...descendants.map(item => item.goal)].flatMap(
              connectedGoal => connectedGoal.project_ids ?? []
            );
            const uniqueProjectIds = [...new Set(connectedGoalIds)];

            return (
              <AgentGoalCard
                key={goal.id}
                descendants={descendants}
                goal={goal}
                projects={uniqueProjectIds.flatMap(projectId => {
                  const project = projectsById.get(projectId);
                  return project ? [project] : [];
                })}
                onOpenGoal={({ goalId }) => store.agentOverlay.openGoal({ goalId })}
                onOpenProject={({ projectId }) => store.agentOverlay.openProject({ projectId })}
              />
            );
          })}
        </StyledGrid>
      )}
    </AgentPageLayout>
  );
});

const StyledGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[5]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
